import React from 'react';
import { BookOpen, Map, Target, Layers, ArrowRight, Zap, CheckCircle2, Award, Crosshair, Heart, Link, Activity } from 'lucide-react';

export default function Guide() {
  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8 animate-fade-in">
      {/* Header section */}
      <div className="bg-white dark:bg-gradient-to-br dark:from-slate-800 dark:to-slate-900 rounded-3xl p-8 text-slate-800 dark:text-white shadow-sm dark:shadow-xl relative overflow-hidden border border-slate-200 dark:border-slate-700/50">
        <div className="absolute top-0 right-0 p-8 opacity-5">
          <BookOpen className="w-48 h-48 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="relative z-10 max-w-3xl">
          <div className="inline-block px-3 py-1 mb-4 rounded-full bg-blue-50 border border-blue-100 text-blue-600 dark:bg-blue-500/20 dark:border-blue-500/30 dark:text-blue-300 text-sm font-medium tracking-wider">GUIDELINES</div>
          <h1 className="text-2xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-slate-800 to-slate-600 dark:from-white dark:to-slate-300">思序GEO 系统使用指导方针</h1>
          <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed mt-2">
            思序GEO 是一款专注于生成式 AI 引擎优化（Generative Engine Optimization）的品牌数据监控与分析平台。
            本指南将带您快速了解系统的核心概念与操作流程，帮助您更好地追踪和优化品牌在各大 AI 引擎中的表现。
          </p>
        </div>
      </div>

      {/* Core Concepts */}
      <div>
        <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-6 flex items-center">
          <Map className="w-6 h-6 mr-3 text-blue-500" />
          系统核心概念
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/30 rounded-xl flex items-center justify-center mb-4">
              <Target className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">GEO (生成式引擎优化)</h3>
            <p className="text-gray-500 dark:text-slate-400 text-sm">
              传统 SEO 优化的是搜索引擎排名，而 GEO 旨在提升品牌在 ChatGPT、Kimi、DeepSeek 等生成式 AI 引擎回答中的可见度、正面情感与品牌声量。
            </p>
          </div>
          
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl flex items-center justify-center mb-4">
              <Layers className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">监控矩阵模型</h3>
            <p className="text-gray-500 dark:text-slate-400 text-sm">
              系统通过 <strong>“提示词库 × 目标受众（人设） × AI引擎”</strong> 的笛卡尔乘积构建抓取任务，模拟真实用户的各类提问场景。
            </p>
          </div>

          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-12 h-12 bg-purple-50 dark:bg-purple-900/30 rounded-xl flex items-center justify-center mb-4">
              <Zap className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">API与RPA双通道抓取</h3>
            <p className="text-gray-500 dark:text-slate-400 text-sm">
              系统支持官方大模型 API 直连与基于 RPA 的深度抓取。API 通道高效快速；RPA 通道则能模拟真实用户行为，抓取网页端独有的媒体引用（Citations）等深度数据。
            </p>
          </div>
        </div>
      </div>

      {/* Dimension Definitions */}
      <div className="mt-8 mb-8">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-6 flex items-center">
          <BookOpen className="w-6 h-6 mr-3 text-emerald-500" />
          分析维度定义
        </h2>
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 border border-gray-100 dark:border-slate-700 shadow-sm space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            
            <div className="space-y-2">
              <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center">
                <Target className="w-5 h-5 text-blue-500 mr-2" />品牌提及率
              </h3>
              <p className="text-sm text-gray-600 dark:text-slate-400 leading-relaxed bg-slate-50 dark:bg-slate-900/50 p-3 rounded-lg border border-slate-100 dark:border-slate-700">
                <strong className="text-gray-700 dark:text-slate-300">公式：</strong>AI回答中包含目标品牌的次数（同1回答多次计为1次） / 提问总次数<br/>
                <strong className="text-gray-700 dark:text-slate-300">说明：</strong>品牌提及率中，不特殊排除负面展示；此处品牌为字典录入品牌。
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center">
                <Award className="w-5 h-5 text-purple-500 mr-2" />首选占位率
              </h3>
              <p className="text-sm text-gray-600 dark:text-slate-400 leading-relaxed bg-slate-50 dark:bg-slate-900/50 p-3 rounded-lg border border-slate-100 dark:border-slate-700">
                <strong className="text-gray-700 dark:text-slate-300">公式：</strong>目标品牌在AI回答中处于首个推荐位置（TOP1）的次数 / 提问总次数
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center">
                <Crosshair className="w-5 h-5 text-indigo-500 mr-2" />高位曝光率
              </h3>
              <p className="text-sm text-gray-600 dark:text-slate-400 leading-relaxed bg-slate-50 dark:bg-slate-900/50 p-3 rounded-lg border border-slate-100 dark:border-slate-700">
                <strong className="text-gray-700 dark:text-slate-300">公式：</strong>目标品牌在AI回答中处于前三个推荐位置（TOP3）的次数 / 提问总次数
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center">
                <Heart className="w-5 h-5 text-teal-500 mr-2" />情感健康度
              </h3>
              <p className="text-sm text-gray-600 dark:text-slate-400 leading-relaxed bg-slate-50 dark:bg-slate-900/50 p-3 rounded-lg border border-slate-100 dark:border-slate-700">
                <strong className="text-gray-700 dark:text-slate-300">公式：</strong>AI搜索结果中正向信息条数（不包含中性、争议、负向） / 总信息条数
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center">
                <Link className="w-5 h-5 text-orange-500 mr-2" />媒体引用率
              </h3>
              <p className="text-sm text-gray-600 dark:text-slate-400 leading-relaxed bg-slate-50 dark:bg-slate-900/50 p-3 rounded-lg border border-slate-100 dark:border-slate-700">
                <strong className="text-gray-700 dark:text-slate-300">公式：</strong>回答中品牌相关媒体引用数量（同1文章多次记为1次） / 所有引用媒体数量
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center">
                <Activity className="w-5 h-5 text-rose-500 mr-2" />自有命中率
              </h3>
              <p className="text-sm text-gray-600 dark:text-slate-400 leading-relaxed bg-slate-50 dark:bg-slate-900/50 p-3 rounded-lg border border-slate-100 dark:border-slate-700">
                <strong className="text-gray-700 dark:text-slate-300">公式：</strong>AI回答的引用媒体源中，自有媒体矩阵 / 所有引用媒体数量
              </p>
            </div>

          </div>
        </div>
      </div>

      {/* Workflow */}
      <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 border border-gray-100 dark:border-slate-700 shadow-sm">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-8">🚀 标准操作流程 (SOP)</h2>
        
        <div className="space-y-8">
          {/* Step 1 */}
          <div className="flex">
            <div className="flex flex-col items-center mr-6">
              <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold shadow-md">1</div>
              <div className="w-0.5 h-full bg-gray-200 dark:bg-slate-700 my-2"></div>
            </div>
            <div className="pb-8">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">配置基础业务模型</h3>
              <p className="text-gray-600 dark:text-slate-400 mb-4">在开始抓取之前，需要先建立监控标的。</p>
              <ul className="space-y-2 text-sm text-gray-500 dark:text-slate-400">
                <li className="flex items-center"><CheckCircle2 className="w-4 h-4 mr-2 text-green-500" /> <strong>GEO 业务配置 - 品牌与竞品配置：</strong>设置您的品牌及其核心竞争对手。</li>
                <li className="flex items-center"><CheckCircle2 className="w-4 h-4 mr-2 text-green-500" /> <strong>GEO 业务配置 - 抓取词库管理：</strong>导入您希望监控的用户搜索关键词与目标人设。</li>
                <li className="flex items-center"><CheckCircle2 className="w-4 h-4 mr-2 text-green-500" /> <strong>GEO 业务配置 - 引擎与抓取配置：</strong>选择您需要覆盖的 AI 引擎（如 DeepSeek、Kimi）。</li>
              </ul>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex">
            <div className="flex flex-col items-center mr-6">
              <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold shadow-md">2</div>
              <div className="w-0.5 h-full bg-gray-200 dark:bg-slate-700 my-2"></div>
            </div>
            <div className="pb-8">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">下发与调度任务</h3>
              <p className="text-gray-600 dark:text-slate-400 mb-4">将配置好的业务模型转化为实际的执行任务。</p>
              <ul className="space-y-2 text-sm text-gray-500 dark:text-slate-400">
                <li className="flex items-center"><CheckCircle2 className="w-4 h-4 mr-2 text-green-500" /> 进入 <strong>任务调度中心 - 定时抓取任务</strong>。</li>
                <li className="flex items-center"><CheckCircle2 className="w-4 h-4 mr-2 text-green-500" /> 点击“新建任务”，选择项目、品牌集、词集、人设以及目标引擎。</li>
                <li className="flex items-center"><CheckCircle2 className="w-4 h-4 mr-2 text-green-500" /> 系统会自动计算所需额度（Tokens/Costs），确认无误后下发执行。</li>
              </ul>
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex">
            <div className="flex flex-col items-center mr-6">
              <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold shadow-md">3</div>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">分析与洞察大盘</h3>
              <p className="text-gray-600 dark:text-slate-400 mb-4">等待任务完成后，前往数据大盘进行多维分析。</p>
              <ul className="space-y-2 text-sm text-gray-500 dark:text-slate-400">
                <li className="flex items-center"><CheckCircle2 className="w-4 h-4 mr-2 text-green-500" /> <strong>引擎表现大盘：</strong>横向对比各大 AI 引擎对您品牌的友好度，查看推荐率、SOV等核心 KPI。</li>
                <li className="flex items-center"><CheckCircle2 className="w-4 h-4 mr-2 text-green-500" /> <strong>品牌数据洞察：</strong>纵向深挖本品牌的情感分布倾向与具体的长尾槽点（负面反馈内容溯源）。</li>
                <li className="flex items-center"><CheckCircle2 className="w-4 h-4 mr-2 text-green-500" /> <strong>对比分析报告：</strong>与竞品展开全面交锋的雷达图和 SWOT 矩阵对比。</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      

    </div>
  );
}
